import api from '@/lib/api';
import Button from '@/components/Button';
import SimpleTextInput from '@/components/SimpleTextInput';
import * as React from 'react';
import { FaFacebookF } from 'react-icons/fa';
import { TbArrowBackUp, TbMail } from 'react-icons/tb';
import { useSearchParams } from 'react-router-dom';

export type SignInSignUpFormMode =
	| 'signIn'
	| 'signUp'
	| 'forgotPassword'
	| 'magic';

export interface SignInSignUpFormProps {
	values: {
		email: string;
		password: string;
		name?: string;
		confirmPassword?: string;
	};
	handleChange: React.ChangeEventHandler<HTMLInputElement>;
	handleSubmit: React.FormEventHandler<HTMLFormElement>;
	handleBlur: React.FocusEventHandler<HTMLInputElement>;
	mode: SignInSignUpFormMode;
	setMode?: (mode: SignInSignUpFormMode) => void;
	isSubmitting: boolean;
	errors: { email?: string; password?: string; name?: string };
	touched: { email?: boolean; password?: boolean; name?: boolean };
	setFieldError: (field: string, message: string | undefined) => void;
	authUrl?: string;
	/** Show the social login buttons; off by default, like the old Plasmic form. */
	showSocialAuth?: boolean;
	/** Magic mode only: the link was sent, swap the form for a confirmation. */
	magicLinkSent?: boolean;
}

function XLogoIcon({ className = '' }: { className?: string }) {
	return (
		<svg
			xmlns='http://www.w3.org/2000/svg'
			fill='currentColor'
			viewBox='0 0 32 32'
			className={className}
			aria-hidden='true'
		>
			<path d='M12.581 17.935L1.008 31.109H3.75l10.053-11.437 8.024 11.437h9.256L18.947 13.813 31.083 0h-2.741l-10.61 12.08L9.257 0H0m27.352 29.084H23.14L3.752 1.925h4.213' />
		</svg>
	);
}

function ProProBolt({ className = '' }: { className?: string }) {
	// Vector13 from the original Plasmic ProProLabel
	return (
		<svg
			xmlns='http://www.w3.org/2000/svg'
			fill='none'
			viewBox='0 0 10 12'
			className={className}
			aria-hidden='true'
		>
			<path
				fill='currentColor'
				d='M.148 1.628L3.398 0l5.927 3.7v4.128L6.796 9.606 3.398 7.538l3.398-1.92-6.648-3.99z'
			/>
		</svg>
	);
}

function ProProSliver({ className = '' }: { className?: string }) {
	// Vector14 from the original Plasmic ProProLabel
	return (
		<svg
			xmlns='http://www.w3.org/2000/svg'
			fill='none'
			viewBox='0 0 4 8'
			className={className}
			aria-hidden='true'
		>
			<path fill='currentColor' d='M0 7.683V0l3.105 1.773v3.99L0 7.682z' />
		</svg>
	);
}

function SocialButton({
	onClick,
	label,
	children,
}: {
	onClick: () => void;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<button
			type='button'
			aria-label={label}
			title={label}
			onClick={onClick}
			className='inline-flex h-11 items-center justify-center rounded-md bg-secondary text-secondary-foreground shadow-sm transition-all duration-150 hover:-translate-y-px hover:bg-secondary/80 hover:shadow-md active:translate-y-0 active:shadow-inner-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
		>
			{children}
		</button>
	);
}

function Field({
	label,
	error,
	children,
}: {
	label: string;
	error?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<div>
			<label className='mb-1.5 block text-sm font-medium text-foreground'>
				{label}
			</label>
			{children}
			{error ? (
				<p className='mt-1.5 text-xs text-red-400' role='alert'>
					{error}
				</p>
			) : null}
		</div>
	);
}

/* Per-app logos (the old Plasmic form's logoAndText block), keyed by the
 * ?appName= OAuth param normalized to lowercase letters only. mapMap's
 * other spellings (mapMapWeb, mapMapDesktopAlt…) prefix-match to mapmap. */
const APP_LOGOS: Record<string, { src: string; size: string }> = {
	hubhub: {
		src: '/plasmic/pro_pro/images/hubhubHPng2.png',
		size: 'h-12', // portrait mark (411x609)
	},
	mapmap: {
		src: '/plasmic/pro_pro/images/mapMapLogoRoughDarksvg.svg',
		size: 'h-10', // roughly square (viewBox 89x82)
	},
	shortstop: {
		src: '/plasmic/pro_pro/images/shortStopLogoWithText.svg',
		size: 'h-10', // mark + wordmark lockup (198x46)
	},
	stampstamp: {
		src: '/plasmic/pro_pro/images/stampStampLogo.png',
		size: 'h-7', // wide mark + wordmark banner (1443x231)
	},
};

/* Per-app accent for the alternate-form action links (Sign In / Sign Up /
 * reset your password / Back to login), keyed like APP_LOGOS. hubhub's
 * blue is also the default for a missing or unrecognized ?appName=. */
const APP_ACCENTS: Record<string, string> = {
	hubhub: '#4761d4',
	mapmap: '#4ADE80',
	shortstop: '#eec25b',
	stampstamp: '#eec25b',
};

function SignInSignUpForm_(
	{
		values,
		handleChange,
		handleSubmit,
		handleBlur,
		mode,
		setMode,
		isSubmitting,
		errors,
		touched,
		setFieldError,
		authUrl,
		showSocialAuth = false,
		magicLinkSent = false,
	}: SignInSignUpFormProps,
	ref: React.Ref<HTMLDivElement>
) {
	const [searchParams] = useSearchParams();

	const appNameParam = searchParams.get('appName') ?? '';
	const normalizedAppName = appNameParam.replace(/[^a-zA-Z]/g, '').toLowerCase();
	const appKey = normalizedAppName.startsWith('mapmap')
		? 'mapmap'
		: normalizedAppName;
	const appLogo = APP_LOGOS[appKey];
	const appAccent = APP_ACCENTS[appKey] ?? APP_ACCENTS.hubhub;

	const isSignUp = mode === 'signUp';
	const isForgotPassword = mode === 'forgotPassword';
	const isMagic = mode === 'magic';
	const magicSent = isMagic && magicLinkSent;

	const submitLabel = isForgotPassword
		? 'Send Email'
		: isMagic
			? 'Send Magic Link'
			: isSignUp
				? 'Sign Up'
				: 'Sign In';

	/* Social buttons start the backend's OAuth flows, so they carry the same
	 * client context the password flow uses (client_id/redirect_uri/state from
	 * the URL) — the backend embeds it in the provider callback to redirect
	 * back to the right app afterwards. */
	const socialAuthUrl = (provider: string) => {
		const params = new URLSearchParams();
		const clientId = searchParams.get('client_id');
		const redirectUri = searchParams.get('redirect_uri');
		const oauthState = searchParams.get('state');
		if (clientId) params.set('clientId', clientId);
		if (redirectUri) params.set('redirectUri', redirectUri);
		if (oauthState) params.set('state', oauthState);
		const query = params.toString();
		return `${api.defaults.baseURL}auth/${provider}${query ? `?${query}` : ''}`;
	};

	return (
		<div
			ref={ref}
			className='relative flex min-h-screen items-center justify-center bg-background px-4 py-12'
		>
			<div className='relative w-full max-w-md'>
				{/* App logo above the card, outside it — like the old Plasmic
				form's logoAndText block's position on the page. Only renders
				when a recognized ?appName= is present; no default. */}
				{appLogo ? (
					<img
						src={appLogo.src}
						alt=''
						aria-hidden='true'
						className={`mx-auto mb-6 block w-auto max-w-full object-contain ${appLogo.size}`}
					/>
				) : null}
				{/* ProPro label — exact replica of the old Plasmic ProProLabel:
				the 320x28 glassy tab hanging flush above the card's top edge */}
				<div className='flex w-full justify-center'>
					<div className='relative h-[28px] w-[320px] top-[1.5px] shrink-0 bg-[url("/plasmic/pro_pro/images/proProLabelBackground.svg")] bg-cover bg-center'>
						<div className="absolute left-[55px] top-[6px] h-[19px] w-[205px] font-['Rubik',_sans-serif]">
							<ProProBolt className='absolute left-[99px] top-[2px] h-[12px] w-[10px] text-[#1a1a1d]' />
							<ProProSliver className='absolute left-[100px] top-[11px] h-[8px] w-[4px] text-[#fbf7ef]' />
							<span className='absolute left-[54.65%] top-[10.68%] w-[11.63%] text-[12.909px] font-semibold leading-[100%] tracking-[-0.06em] text-[#1a1a1d]'>
								Pro
							</span>
							<span className='absolute left-[64.9%] top-[10.68%] w-[11.63%] text-[12.909px] font-medium leading-[100%] tracking-[-0.06em] text-[#faf7ee]'>
								Pro
							</span>
							<span className='absolute left-0 top-0 text-[11.16px] font-medium leading-[17.857px] text-[#fcfaf6]'>
								Sign {isSignUp ? 'up' : 'in'} with your
							</span>
							<span className='absolute left-[160px] top-[1px] text-[11.16px] font-medium leading-[17.857px] text-[#fcfaf6]'>
								account
							</span>
						</div>
					</div>
				</div>
				{/* Raised plastic card. The [--card:...] class locally raises --card to
				multisearch grey's 0.20 (black mode keeps --card = --background, so
				from-card alone paints nothing) — delete it to revert to the flat card.
				--app-accent (per-app link color) is set via style because Tailwind
				can't compile a dynamically-interpolated arbitrary value. */}
				<div
					className='rounded-xl border border-border/50 bg-gradient-to-b from-card to-card/80 p-8 shadow-card [--card:0.20_0.005_285]'
					style={{ '--app-accent': appAccent } as React.CSSProperties}
				>
					{isForgotPassword ? (
						<>
							<h1 className='mt-6 text-center text-xl font-semibold text-foreground'>
								Reset your password
							</h1>
							<p className='mt-2 text-center text-sm text-muted-foreground'>
								We will send you an email to reset your password
							</p>
						</>
					) : null}

					<form onSubmit={handleSubmit} noValidate className='mt-6 space-y-4'>
						{magicSent ? (
							/* Magic link sent — swap the fields for a confirmation. The
							form element stays so the card's spacing keeps working. */
							<div className='text-center'>
								<TbMail
									className='mx-auto h-10 w-10 text-muted-foreground'
									aria-hidden='true'
								/>
								<h2 className='mt-4 text-lg font-semibold text-foreground'>
									Check your email
								</h2>
								<p className='mt-2 text-sm text-muted-foreground'>
									We sent a magic link to{' '}
									<span className='font-medium text-foreground'>{values.email}</span>.
									It expires in 10 minutes.
								</p>
								<p className='mt-6 text-sm text-muted-foreground'>
									<button
										type='button'
										className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setMode?.('signIn');
										}}
									>
										<TbArrowBackUp className='mr-1 -mb-0.5 inline-block h-4 w-4' />
										Use Password
									</button>
								</p>
							</div>
						) : (
						<>
						{isSignUp ? (
							<Field
								label='Name'
								error={errors.name && touched.name && errors.name}
							>
								<SimpleTextInput
									id='name'
									placeholder='Name'
									className='h-11 w-full'
									value={values.name}
									onChange={handleChange}
									onBlur={handleBlur}
									name='name'
									type='text'
									autoComplete='name'
								/>
							</Field>
						) : null}

						<Field
							label='Email address'
							error={errors.email && touched.email && errors.email}
						>
							<SimpleTextInput
								id='email'
								placeholder='Email address'
								className='h-11 w-full'
								value={values.email}
								onChange={handleChange}
								onBlur={handleBlur}
								name='email'
								type='email'
								autoComplete='email'
							/>
						</Field>

						{!isForgotPassword && !isMagic ? (
							<Field
								label='Password'
								error={errors.password && touched.password && errors.password}
							>
								<SimpleTextInput
									id='password'
									placeholder='Password'
									className='h-11 w-full'
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									name='password'
									type='password'
									autoComplete={isSignUp ? 'new-password' : 'current-password'}
								/>
							</Field>
						) : null}

						{isSignUp ? (
							<Field label='Confirm password'>
								<SimpleTextInput
									id='confirmPassword'
									placeholder='Confirm Password'
									className='h-11 w-full'
									value={values?.confirmPassword}
									onChange={handleChange}
									onBlur={handleBlur}
									name='confirmPassword'
									type='password'
									autoComplete='new-password'
								/>
							</Field>
						) : null}

						<Button type='submit' isLoading={isSubmitting} className='h-11 w-full'>
							{submitLabel}
						</Button>

						{/* Recreates the old Plasmic form's "Sign In via Magic Link" /
						"Use Password" toggle between the password and magic flows. */}
						{mode === 'signIn' ? (
							<Button
								type='button'
								variant='secondary'
								className='h-11 w-full'
								onClick={() => setMode?.('magic')}
							>
								Sign In via Magic Link 📨
							</Button>
						) : null}

						{isMagic ? (
							<p className='text-center text-sm text-muted-foreground'>
								<button
									type='button'
									className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										setMode?.('signIn');
									}}
								>
									<TbArrowBackUp className='mr-1 -mb-0.5 inline-block h-4 w-4' />
									Use Password
								</button>
							</p>
						) : null}

						{isForgotPassword ? (
							<p className='text-center text-sm text-muted-foreground'>
								<button
									type='button'
									className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										setMode?.('signIn');
									}}
								>
									<TbArrowBackUp className='mr-1 -mb-0.5 inline-block h-4 w-4' />
									Back to login
								</button>
							</p>
						) : null}
						</>
						)}
					</form>

					{!isForgotPassword && !magicSent ? (
						<>
							{showSocialAuth ? (
								<>
									<p className='mt-6 text-center text-xs text-muted-foreground'>
										Or sign up / sign in with one of these social networks
									</p>
									<div className='mt-3 grid grid-cols-3 gap-3'>
										<SocialButton
											label='Continue with X'
											onClick={() => {
												window.open(socialAuthUrl('twitter'), '_self');
											}}
										>
											<XLogoIcon className='h-4 w-4' />
										</SocialButton>
										<SocialButton
											label='Continue with Google'
											onClick={() => {
												window.open(socialAuthUrl('google'), '_self');
											}}
										>
											<img
												src='/plasmic/pro_pro/images/iconsGoogleCorful.svg'
												alt=''
												className='h-5 w-5'
											/>
										</SocialButton>
										<SocialButton
											label='Continue with Facebook'
											onClick={() => {
												window.open(socialAuthUrl('facebook'), '_self');
											}}
										>
											<FaFacebookF className='h-[18px] w-[18px]' aria-hidden='true' />
										</SocialButton>
									</div>
								</>
							) : null}

							<div className='mt-6 space-y-2 text-center text-sm text-muted-foreground'>
								{isSignUp ? (
									<p>
										Already have an account?{' '}
										<button
											type='button'
											className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
											onClick={() => {
												const signinUrl =
													authUrl?.replace('/signup', '/signin') ||
													`/signin?${searchParams.toString()}`;
												window.location.href = signinUrl;
											}}
										>
											Sign In
										</button>
									</p>
								) : (
									<>
										{!isMagic ? (
											<p>
												And if you need to, you can{' '}
												<button
													type='button'
													className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
													onClick={() => {
														window.location.href = '/reset-password';
													}}
												>
													reset your password
												</button>
											</p>
										) : null}
										<p>
											Don&apos;t have an account?{' '}
											<button
												type='button'
												className='cursor-pointer font-medium text-[color:var(--app-accent)] underline-offset-4 hover:underline'
												onClick={() => {
													const signupUrl =
														authUrl || `/signup?${searchParams.toString()}`;
													window.location.href = signupUrl;
												}}
											>
												Sign Up
											</button>
										</p>
									</>
								)}
							</div>
						</>
					) : null}
				</div>

				<p className='mt-6 text-center text-xs text-muted-foreground'>
					<a
						href='https://hubhub.app/terms-of-service'
						className='underline-offset-2 hover:text-foreground hover:underline'
					>
						Terms of Service
					</a>
					<span className='mx-2' aria-hidden='true'>
						·
					</span>
					<a
						href='https://hubhub.app/privacy-policy'
						className='underline-offset-2 hover:text-foreground hover:underline'
					>
						Privacy Policy
					</a>
				</p>
			</div>
		</div>
	);
}

const SignInSignUpForm = React.forwardRef(SignInSignUpForm_);
export default SignInSignUpForm;
